<template>
  <div class="min-h-screen bg-[#fff8df] px-4 py-6 text-ink-900 sm:px-6">
    <main class="mx-auto max-w-6xl">
      <section class="rounded-2xl border border-jackfruit-200 bg-white p-5 shadow-sm sm:p-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p class="text-sm font-black text-pulp-600">菠萝蜜管理后台</p>
            <h1 class="mt-1 text-2xl font-black text-ink-900 sm:text-3xl">人工审核台</h1>
            <p class="mt-2 max-w-2xl text-sm leading-6 text-ink-500">
              处理创作者上架复审和未通过作品申诉。通过后作品进入公共书库，拒绝后会保留原因给创作者查看。
            </p>
          </div>
          <button
            class="rounded-xl bg-jackfruit-500 px-5 py-2.5 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="loading"
            @click="refreshData"
          >
            {{ loading ? '刷新中' : '刷新列表' }}
          </button>
        </div>

        <div class="mt-5 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <label class="block">
            <span class="text-xs font-black text-ink-500">管理员令牌</span>
            <input
              v-model="adminToken"
              class="mt-1 w-full rounded-xl border border-jackfruit-200 bg-jackfruit-50/40 px-3 py-2.5 text-sm font-bold outline-none focus:border-jackfruit-500"
              placeholder="x-admin-token"
              type="password"
              @change="saveCredentials"
            >
          </label>
          <label class="block">
            <span class="text-xs font-black text-ink-500">管理员密码</span>
            <input
              v-model="adminPassword"
              class="mt-1 w-full rounded-xl border border-jackfruit-200 bg-jackfruit-50/40 px-3 py-2.5 text-sm font-bold outline-none focus:border-jackfruit-500"
              placeholder="x-admin-password"
              type="password"
              @change="saveCredentials"
            >
          </label>
          <button
            class="self-end rounded-xl border border-jackfruit-200 px-5 py-2.5 text-sm font-black text-ink-700 transition hover:bg-jackfruit-50"
            @click="saveCredentials"
          >
            保存凭证
          </button>
        </div>

        <p v-if="message" class="mt-4 rounded-xl bg-leaf-50 px-3 py-2 text-sm font-bold text-leaf-700">{{ message }}</p>
        <p v-if="error" class="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-red-600">{{ error }}</p>
      </section>

      <section class="mt-5 grid gap-3 sm:grid-cols-3">
        <button
          class="rounded-2xl border p-4 text-left transition"
          :class="activeTab === 'reviews' ? 'border-jackfruit-400 bg-white shadow-sm' : 'border-transparent bg-white/70 hover:bg-white'"
          @click="activeTab = 'reviews'"
        >
          <p class="text-sm font-black text-ink-500">待审核作品</p>
          <p class="mt-1 text-3xl font-black text-ink-900">{{ reviewPagination.total }}</p>
        </button>
        <button
          class="rounded-2xl border p-4 text-left transition"
          :class="activeTab === 'appeals' ? 'border-pulp-300 bg-white shadow-sm' : 'border-transparent bg-white/70 hover:bg-white'"
          @click="activeTab = 'appeals'"
        >
          <p class="text-sm font-black text-ink-500">待处理申诉</p>
          <p class="mt-1 text-3xl font-black text-ink-900">{{ appealPagination.total }}</p>
        </button>
        <button
          class="rounded-2xl border p-4 text-left transition"
          :class="activeTab === 'earnings' ? 'border-leaf-300 bg-white shadow-sm' : 'border-transparent bg-white/70 hover:bg-white'"
          @click="activeTab = 'earnings'"
        >
          <p class="text-sm font-black text-ink-500">收益流水</p>
          <p class="mt-1 text-3xl font-black text-ink-900">{{ earningPagination.total }}</p>
        </button>
      </section>

      <section v-if="activeTab !== 'earnings'" class="mt-5 rounded-2xl border border-jackfruit-100 bg-white p-4 shadow-sm sm:p-5">
        <div class="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-xl font-black text-ink-900">{{ activeTab === 'reviews' ? '作品复审' : '申诉复审' }}</h2>
            <p class="mt-1 text-sm text-ink-500">{{ activeTab === 'reviews' ? 'AI 已初筛通过或需要人工确认的公开作品。' : '创作者提交申诉后等待人工复核的作品。' }}</p>
          </div>
          <div class="text-sm font-bold text-ink-400">
            共 {{ currentPagination.total }} 条
          </div>
        </div>

        <div v-if="loading" class="rounded-xl bg-jackfruit-50 px-4 py-8 text-center text-sm font-bold text-ink-500">
          正在加载审核列表
        </div>

        <div v-else-if="currentBooks.length === 0" class="rounded-xl bg-jackfruit-50 px-4 py-8 text-center text-sm font-bold text-ink-500">
          当前没有需要处理的内容
        </div>

        <div v-else class="space-y-4">
          <article
            v-for="book in currentBooks"
            :key="book.id"
            class="grid gap-4 rounded-xl border border-jackfruit-100 bg-[#fffdf4] p-4 md:grid-cols-[96px_1fr]"
          >
            <img
              class="h-32 w-24 rounded-lg object-cover shadow-sm"
              :src="book.coverUrl"
              :alt="book.title"
            >
            <div class="min-w-0">
              <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div class="min-w-0">
                  <h3 class="truncate text-lg font-black text-ink-900">{{ book.title }}</h3>
                  <p class="mt-1 text-sm font-bold text-ink-500">
                    {{ book.author }} · {{ book.totalChapters }} 章 · ID {{ book.id }}
                  </p>
                  <p class="mt-1 text-xs font-bold text-ink-400">
                    创作者：{{ formatOwner(book) }} · 提交于 {{ formatDate(book.createdAt) }}
                  </p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <span class="rounded-full bg-amber-50 px-3 py-1 text-xs font-black text-amber-700">{{ statusText(book) }}</span>
                  <span class="rounded-full bg-jackfruit-50 px-3 py-1 text-xs font-black text-ink-500">自定义 {{ customRatio(book) }}%</span>
                </div>
              </div>

              <p v-if="book.description" class="mt-3 line-clamp-2 text-sm leading-6 text-ink-600">{{ book.description }}</p>

              <div class="mt-3 grid gap-3 lg:grid-cols-2">
                <div class="rounded-xl bg-white px-3 py-2">
                  <p class="text-xs font-black text-ink-400">审核原因 / 命中片段</p>
                  <p class="mt-1 whitespace-pre-wrap text-sm leading-6 text-ink-700">{{ book.reviewReason || '暂无审核说明' }}</p>
                </div>
                <div v-if="activeTab === 'appeals'" class="rounded-xl bg-white px-3 py-2">
                  <p class="text-xs font-black text-ink-400">申诉理由</p>
                  <p class="mt-1 whitespace-pre-wrap text-sm leading-6 text-ink-700">{{ book.appealReason || '暂无申诉说明' }}</p>
                </div>
              </div>

              <div class="mt-4 flex flex-col gap-2 md:flex-row">
                <input
                  v-model="actionReasons[book.id]"
                  class="min-w-0 flex-1 rounded-xl border border-jackfruit-200 bg-white px-3 py-2.5 text-sm font-bold outline-none focus:border-jackfruit-500"
                  :placeholder="activeTab === 'reviews' ? '拒绝时填写原因；通过可留空' : '拒绝申诉时填写原因；通过可留空'"
                >
                <button
                  class="rounded-xl bg-leaf-500 px-4 py-2.5 text-sm font-black text-white transition hover:bg-leaf-600 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="actingBookId === book.id"
                  @click="approveBook(book)"
                >
                  {{ activeTab === 'reviews' ? '审核通过' : '申诉通过' }}
                </button>
                <button
                  class="rounded-xl bg-red-500 px-4 py-2.5 text-sm font-black text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="actingBookId === book.id"
                  @click="rejectBook(book)"
                >
                  {{ activeTab === 'reviews' ? '审核拒绝' : '申诉拒绝' }}
                </button>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-else class="mt-5 rounded-2xl border border-jackfruit-100 bg-white p-4 shadow-sm sm:p-5">
        <div class="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-xl font-black text-ink-900">收益管理</h2>
            <p class="mt-1 text-sm text-ink-500">查看创作者站内收益，处理异常收益的冻结、恢复和冲正。</p>
          </div>
          <div class="text-sm font-bold text-ink-400">共 {{ earningPagination.total }} 条流水</div>
        </div>

        <div v-if="loading" class="rounded-xl bg-jackfruit-50 px-4 py-8 text-center text-sm font-bold text-ink-500">
          正在加载收益数据
        </div>

        <template v-else>
          <div class="mb-5 grid gap-3 lg:grid-cols-3">
            <article
              v-for="balance in balances"
              :key="balance.id"
              class="rounded-xl border border-jackfruit-100 bg-[#fffdf4] p-4"
            >
              <p class="truncate text-sm font-black text-ink-900">{{ formatUser(balance.user) }}</p>
              <p class="mt-1 text-xs font-bold text-ink-400">用户 ID {{ balance.userId }}</p>
              <div class="mt-3 grid grid-cols-2 gap-2 text-xs font-bold text-ink-500">
                <span>累计 {{ balance.totalEarnedBeans }}</span>
                <span>可用 {{ balance.availableBeans }}</span>
                <span>待结算 {{ balance.pendingBeans }}</span>
                <span>冻结 {{ balance.frozenBeans }}</span>
              </div>
            </article>
            <div v-if="balances.length === 0" class="rounded-xl bg-jackfruit-50 px-4 py-8 text-center text-sm font-bold text-ink-500 lg:col-span-3">
              还没有创作者收益余额
            </div>
          </div>

          <div v-if="earnings.length === 0" class="rounded-xl bg-jackfruit-50 px-4 py-8 text-center text-sm font-bold text-ink-500">
            还没有收益流水
          </div>

          <div v-else class="space-y-3">
            <article
              v-for="earning in earnings"
              :key="earning.id"
              class="grid gap-3 rounded-xl border border-jackfruit-100 bg-[#fffdf4] p-4 lg:grid-cols-[1fr_auto]"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="rounded-full px-3 py-1 text-xs font-black" :class="earningStatusClass(earning.status)">
                    {{ earningStatusText(earning.status) }}
                  </span>
                  <span class="rounded-full bg-jackfruit-50 px-3 py-1 text-xs font-black text-ink-600">
                    +{{ earning.honeyBeans }} 蜜豆
                  </span>
                  <span class="text-xs font-bold text-ink-400">流水 ID {{ earning.id }}</span>
                </div>
                <h3 class="mt-2 truncate text-base font-black text-ink-900">{{ earning.book?.title || '未知作品' }}</h3>
                <p class="mt-1 text-sm font-bold text-ink-500">
                  创作者：{{ formatUser(earning.user) }} · {{ earning.chapter ? `第 ${earning.chapter.chapterNumber} 章` : '章节未知' }} · {{ formatDate(earning.createdAt) }}
                </p>
                <p class="mt-1 text-xs font-bold text-ink-400">
                  来源：{{ earning.reason }} · 支付记录 {{ earning.paymentRecordId || '-' }}
                </p>
              </div>
              <div class="flex flex-wrap items-center gap-2 lg:justify-end">
                <button
                  v-if="earning.status === 'settled'"
                  class="rounded-xl bg-amber-500 px-4 py-2 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="actingEarningId === earning.id"
                  @click="runEarningAction(earning, 'freeze')"
                >
                  冻结
                </button>
                <button
                  v-if="earning.status === 'pending' || earning.status === 'frozen'"
                  class="rounded-xl bg-leaf-500 px-4 py-2 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="actingEarningId === earning.id"
                  @click="runEarningAction(earning, 'settle')"
                >
                  恢复入账
                </button>
                <button
                  v-if="earning.status !== 'reversed'"
                  class="rounded-xl bg-red-500 px-4 py-2 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="actingEarningId === earning.id"
                  @click="runEarningAction(earning, 'reverse')"
                >
                  冲正
                </button>
              </div>
            </article>
          </div>
        </template>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useAdminDashboard } from '@/composables/admin/useAdminDashboard'

const {
  activeTab,
  loading,
  actingBookId,
  actingEarningId,
  message,
  error,
  balances,
  earnings,
  actionReasons,
  adminToken,
  adminPassword,
  currentBooks,
  currentPagination,
  saveCredentials,
  refreshData,
  approveBook,
  rejectBook,
  runEarningAction,
  formatUser,
  formatOwner,
  formatDate,
  statusText,
  customRatio,
  earningStatusText,
  earningStatusClass
} = useAdminDashboard()
</script>
